/**
 * Pathogen-record validator. Pure functional; throws nothing — returns
 * a discriminated-union result. Audit gate consumes this to enforce
 * the verifier-first invariant.
 */

import type {
  Pathogen, PathogenClass, PmidCitation, SeverityDenominator, TransmissionMode,
  VaccinePlatform, VerificationStatus,
} from './types.js';

export type ValidationResult =
  | { valid: true; pathogen: Pathogen }
  | { valid: false; errors: string[] };

const PATHOGEN_CLASSES: readonly PathogenClass[] = [
  'virus', 'bacterium', 'parasite', 'fungus', 'prion',
];

const TRANSMISSION_MODES: readonly TransmissionMode[] = [
  'respiratory-droplet', 'respiratory-aerosol', 'vector-mosquito',
  'vector-tick', 'vector-flea', 'vector-fly', 'fecal-oral', 'sexual',
  'bloodborne', 'vertical', 'fomite', 'iatrogenic', 'foodborne',
  'waterborne', 'direct-contact', 'zoonotic-direct',
];

const VACCINE_PLATFORMS: readonly VaccinePlatform[] = [
  'mRNA', 'viral-vector', 'inactivated', 'live-attenuated',
  'subunit', 'conjugate', 'protein-recombinant', 'toxoid',
  'virus-like-particle',
];

const VERIFICATION_STATUSES: readonly VerificationStatus[] = [
  'verified', 'partial', 'unverified',
];

function isFiniteNumber(x: unknown): x is number {
  return typeof x === 'number' && Number.isFinite(x);
}

function validateCitations(c: unknown, path: string, errors: string[]): c is PmidCitation[] {
  if (!Array.isArray(c)) {
    errors.push(`${path}: citations must be an array`);
    return false;
  }
  if (c.length === 0) {
    errors.push(`${path}: citations array must be non-empty (verifier-first invariant)`);
    return false;
  }
  let ok = true;
  for (let i = 0; i < c.length; i++) {
    const cite = c[i];
    if (typeof cite !== 'object' || cite === null) {
      errors.push(`${path}[${i}]: citation must be an object`);
      ok = false; continue;
    }
    const obj = cite as Record<string, unknown>;
    if (typeof obj.pmid !== 'string' || obj.pmid.length === 0) {
      errors.push(`${path}[${i}]: pmid must be a non-empty string`);
      ok = false;
    } else if (!/^\d+$/.test(obj.pmid)) {
      errors.push(`${path}[${i}]: pmid must be digit-only ('${obj.pmid}' supplied)`);
      ok = false;
    }
  }
  return ok;
}

function validateCitedScalar(v: unknown, path: string, errors: string[]): boolean {
  if (typeof v !== 'object' || v === null) {
    errors.push(`${path}: must be an object`); return false;
  }
  const obj = v as Record<string, unknown>;
  let ok = true;
  if (!isFiniteNumber(obj.value)) { errors.push(`${path}.value: must be a finite number`); ok = false; }
  if (!validateCitations(obj.citations, `${path}.citations`, errors)) ok = false;
  return ok;
}

function validateCitedFraction(v: unknown, path: string, errors: string[]): boolean {
  if (!validateCitedScalar(v, path, errors)) return false;
  const obj = v as { value: number };
  if (obj.value < 0 || obj.value > 1) {
    errors.push(`${path}.value: fraction must be in [0, 1] (${obj.value} supplied)`);
    return false;
  }
  return true;
}

function validateCitedRange(v: unknown, path: string, errors: string[]): boolean {
  if (typeof v !== 'object' || v === null) {
    errors.push(`${path}: must be an object`); return false;
  }
  const obj = v as Record<string, unknown>;
  let ok = true;
  if (!isFiniteNumber(obj.value)) { errors.push(`${path}.value: must be finite`); ok = false; }
  if (!isFiniteNumber(obj.low)) { errors.push(`${path}.low: must be finite`); ok = false; }
  if (!isFiniteNumber(obj.high)) { errors.push(`${path}.high: must be finite`); ok = false; }
  if (ok) {
    const lo = obj.low as number, hi = obj.high as number, val = obj.value as number;
    if (lo > hi) { errors.push(`${path}: low (${lo}) must be ≤ high (${hi})`); ok = false; }
    if (val < lo || val > hi) {
      errors.push(`${path}: value (${val}) outside [low ${lo}, high ${hi}]`); ok = false;
    }
  }
  if (!validateCitations(obj.citations, `${path}.citations`, errors)) ok = false;
  return ok;
}

const SEVERITY_DENOMINATORS: readonly SeverityDenominator[] = [
  'infection-fatality', 'case-fatality', 'severe-case-fatality', 'hospitalization',
];

/**
 * A severity ratio: a CitedRange (value within [low, high], non-empty
 * citations) constrained to [0, 1], plus a mandatory `kind` discriminator
 * and a non-empty verbatim `note`. The note is the auditable evidence for
 * this clinical-adjacent number, so it is required.
 */
function validateSeverityRange(v: unknown, path: string, errors: string[]): boolean {
  if (!validateCitedRange(v, path, errors)) return false;
  const obj = v as Record<string, unknown>;
  let ok = true;
  for (const k of ['value', 'low', 'high'] as const) {
    const n = obj[k] as number;
    if (n < 0 || n > 1) {
      errors.push(`${path}.${k}: severity ratio must be a fraction in [0, 1] (${n} supplied)`);
      ok = false;
    }
  }
  if (!SEVERITY_DENOMINATORS.includes(obj.kind as SeverityDenominator)) {
    errors.push(`${path}.kind: must be one of ${SEVERITY_DENOMINATORS.join(', ')} ('${String(obj.kind)}' supplied)`);
    ok = false;
  }
  if (typeof obj.note !== 'string' || obj.note.trim().length === 0) {
    errors.push(`${path}.note: must be a non-empty string (verbatim source quote + denominator scope)`);
    ok = false;
  }
  return ok;
}

function validateIfrAgeBand(b: unknown, path: string, errors: string[]): boolean {
  if (typeof b !== 'object' || b === null) {
    errors.push(`${path}: must be object`); return false;
  }
  const obj = b as Record<string, unknown>;
  let ok = true;
  if (!isFiniteNumber(obj.ageBandLow)) { errors.push(`${path}.ageBandLow: must be finite`); ok = false; }
  if (typeof obj.ageBandHigh !== 'number') { errors.push(`${path}.ageBandHigh: must be number`); ok = false; }
  if (!isFiniteNumber(obj.ifr)) { errors.push(`${path}.ifr: must be finite`); ok = false; }
  else if ((obj.ifr as number) < 0 || (obj.ifr as number) > 1) {
    errors.push(`${path}.ifr: must be in [0, 1] (${obj.ifr} supplied)`); ok = false;
  }
  if (!validateCitations(obj.citations, `${path}.citations`, errors)) ok = false;
  return ok;
}

function validateTestSensitivityCurve(v: unknown, path: string, errors: string[]): boolean {
  if (typeof v !== 'object' || v === null) {
    errors.push(`${path}: must be an object`); return false;
  }
  const obj = v as Record<string, unknown>;
  let ok = true;
  if (typeof obj.assay !== 'string' || obj.assay.length === 0) {
    errors.push(`${path}.assay: must be a non-empty string`); ok = false;
  }
  if (obj.daysReference !== 'infection' && obj.daysReference !== 'symptom-onset') {
    errors.push(`${path}.daysReference: must be 'infection' or 'symptom-onset'`); ok = false;
  }
  if (!Array.isArray(obj.points) || obj.points.length === 0) {
    errors.push(`${path}.points: must be a non-empty array`); return false;
  }
  let prevDay = -Infinity;
  for (let i = 0; i < obj.points.length; i++) {
    const pt = obj.points[i];
    const ppath = `${path}.points[${i}]`;
    if (typeof pt !== 'object' || pt === null) {
      errors.push(`${ppath}: must be an object`); ok = false; continue;
    }
    const p = pt as Record<string, unknown>;
    if (!isFiniteNumber(p.daysSinceInfection)) {
      errors.push(`${ppath}.daysSinceInfection: must be a finite number`); ok = false;
    } else {
      if ((p.daysSinceInfection as number) < prevDay) {
        errors.push(`${ppath}.daysSinceInfection: points must be sorted ascending`); ok = false;
      }
      prevDay = p.daysSinceInfection as number;
    }
    if (!isFiniteNumber(p.sensitivity)) {
      errors.push(`${ppath}.sensitivity: must be a finite number`); ok = false;
    } else if ((p.sensitivity as number) < 0 || (p.sensitivity as number) > 1) {
      errors.push(`${ppath}.sensitivity: must be in [0, 1] (${p.sensitivity} supplied)`); ok = false;
    }
    if (!validateCitations(p.citations, `${ppath}.citations`, errors)) ok = false;
  }
  return ok;
}

export function validatePathogen(rawJson: string, sourcePath: string): ValidationResult {
  const errors: string[] = [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawJson);
  } catch (e) {
    return { valid: false, errors: [`${sourcePath}: JSON parse error — ${(e as Error).message}`] };
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return { valid: false, errors: [`${sourcePath}: top-level must be an object`] };
  }
  const r = parsed as Record<string, unknown>;

  // --- id, names ---
  if (typeof r.id !== 'string' || r.id.length === 0) errors.push('id must be non-empty string');
  if (typeof r.names !== 'object' || r.names === null) errors.push('names must be an object');
  else {
    const n = r.names as Record<string, unknown>;
    if (typeof n.common !== 'string' || n.common.length === 0) errors.push('names.common required');
    if (typeof n.scientific !== 'string' || n.scientific.length === 0) errors.push('names.scientific required');
  }

  // --- class, taxonomy ---
  if (!PATHOGEN_CLASSES.includes(r.pathogenClass as PathogenClass)) {
    errors.push(`pathogenClass must be one of ${PATHOGEN_CLASSES.join(', ')}`);
  }
  if (typeof r.taxonomy !== 'object' || r.taxonomy === null) errors.push('taxonomy required');
  else {
    const t = r.taxonomy as Record<string, unknown>;
    if (typeof t.family !== 'string') errors.push('taxonomy.family required');
    if (typeof t.genus !== 'string') errors.push('taxonomy.genus required');
    if (typeof t.species !== 'string') errors.push('taxonomy.species required');
  }

  // --- transmission ---
  if (typeof r.transmission !== 'object' || r.transmission === null) errors.push('transmission required');
  else {
    const tr = r.transmission as Record<string, unknown>;
    if (!Array.isArray(tr.modes) || tr.modes.length === 0) {
      errors.push('transmission.modes must be non-empty array');
    } else {
      for (const m of tr.modes) {
        if (!TRANSMISSION_MODES.includes(m as TransmissionMode)) {
          errors.push(`transmission.modes: '${m}' not in vocabulary`);
        }
      }
    }
    if (typeof tr.primaryRoute !== 'string') errors.push('transmission.primaryRoute required');
  }

  // --- core epi parameters (required) ---
  validateCitedRange(r.R0, 'R0', errors);
  validateCitedRange(r.generationTimeDays, 'generationTimeDays', errors);
  validateCitedScalar(r.latentPeriodDays, 'latentPeriodDays', errors);
  validateCitedScalar(r.infectiousPeriodDays, 'infectiousPeriodDays', errors);

  if (typeof r.R0 === 'object' && r.R0 !== null) {
    const v = (r.R0 as Record<string, unknown>).value;
    if (typeof v === 'number' && (v < 0 || v > 25)) {
      errors.push(`R0.value: ${v} outside biological plausibility (0..25)`);
    }
  }
  if (typeof r.latentPeriodDays === 'object' && r.latentPeriodDays !== null) {
    const v = (r.latentPeriodDays as Record<string, unknown>).value;
    if (typeof v === 'number' && v < 0) errors.push('latentPeriodDays must be ≥ 0');
  }
  if (typeof r.infectiousPeriodDays === 'object' && r.infectiousPeriodDays !== null) {
    const v = (r.infectiousPeriodDays as Record<string, unknown>).value;
    if (typeof v === 'number' && v <= 0) errors.push('infectiousPeriodDays must be > 0');
  }

  // Latent ≤ generation time (when both present and reasonable).
  if (typeof r.latentPeriodDays === 'object' && r.latentPeriodDays !== null
   && typeof r.generationTimeDays === 'object' && r.generationTimeDays !== null) {
    const lat = (r.latentPeriodDays as Record<string, unknown>).value;
    const gen = (r.generationTimeDays as Record<string, unknown>).value;
    if (typeof lat === 'number' && typeof gen === 'number' && lat > gen * 1.5) {
      errors.push(`latentPeriodDays (${lat}) > 1.5 × generationTimeDays (${gen}) — implausible`);
    }
  }

  // --- optional cited fractions ---
  if (r.preSymptomaticTransmission !== undefined) {
    validateCitedFraction(r.preSymptomaticTransmission, 'preSymptomaticTransmission', errors);
  }
  if (r.asymptomaticFraction !== undefined) {
    validateCitedFraction(r.asymptomaticFraction, 'asymptomaticFraction', errors);
  }
  if (r.icuFraction !== undefined) {
    validateCitedFraction(r.icuFraction, 'icuFraction', errors);
  }

  // --- dispersionK (optional CitedRange) ---
  if (r.dispersionK !== undefined) {
    if (validateCitedRange(r.dispersionK, 'dispersionK', errors)) {
      const k = (r.dispersionK as Record<string, unknown>).value as number;
      const lo = (r.dispersionK as Record<string, unknown>).low as number;
      if (lo <= 0) errors.push(`dispersionK.low must be > 0 (${lo} supplied)`);
      if (k <= 0) errors.push(`dispersionK.value must be > 0 (${k} supplied)`);
    }
  }

  // --- testSensitivityOverTime (optional curve) ---
  if (r.testSensitivityOverTime !== undefined) {
    validateTestSensitivityCurve(r.testSensitivityOverTime, 'testSensitivityOverTime', errors);
  }

  // --- severity (optional rollup: cfr / ifr / hospitalizationRate) ---
  if (r.severity !== undefined) {
    if (typeof r.severity !== 'object' || r.severity === null) {
      errors.push('severity: must be an object');
    } else {
      const sev = r.severity as Record<string, unknown>;
      if (sev.cfr !== undefined) validateSeverityRange(sev.cfr, 'severity.cfr', errors);
      if (sev.ifr !== undefined) validateSeverityRange(sev.ifr, 'severity.ifr', errors);
      if (sev.hospitalizationRate !== undefined) {
        validateSeverityRange(sev.hospitalizationRate, 'severity.hospitalizationRate', errors);
      }
      if (sev.notes !== undefined && typeof sev.notes !== 'string') {
        errors.push('severity.notes: must be a string when present');
      }
      // Honesty rail: cfr should declare a case/severe-case denominator,
      // ifr should declare infection-fatality — catch a mislabelled kind.
      const cfr = sev.cfr as Record<string, unknown> | undefined;
      if (cfr && cfr.kind === 'infection-fatality') {
        errors.push("severity.cfr.kind: a 'cfr' field must not use kind 'infection-fatality' (put it under 'ifr')");
      }
      const ifr = sev.ifr as Record<string, unknown> | undefined;
      if (ifr && ifr.kind !== undefined && ifr.kind !== 'infection-fatality') {
        errors.push(`severity.ifr.kind: an 'ifr' field must use kind 'infection-fatality' ('${String(ifr.kind)}' supplied)`);
      }
      const hosp = sev.hospitalizationRate as Record<string, unknown> | undefined;
      if (hosp && hosp.kind !== undefined && hosp.kind !== 'hospitalization') {
        errors.push(`severity.hospitalizationRate.kind: must use kind 'hospitalization' ('${String(hosp.kind)}' supplied)`);
      }
    }
  }

  // --- IFR ---
  if (!Array.isArray(r.ifrByAgeBand) || r.ifrByAgeBand.length === 0) {
    errors.push('ifrByAgeBand must be non-empty array');
  } else {
    for (let i = 0; i < r.ifrByAgeBand.length; i++) {
      validateIfrAgeBand(r.ifrByAgeBand[i], `ifrByAgeBand[${i}]`, errors);
    }
  }

  // --- vaccines (optional) ---
  if (r.vaccines !== undefined) {
    if (!Array.isArray(r.vaccines)) {
      errors.push('vaccines must be array');
    } else {
      for (let i = 0; i < r.vaccines.length; i++) {
        const v = r.vaccines[i] as Record<string, unknown>;
        if (typeof v.name !== 'string') errors.push(`vaccines[${i}].name required`);
        if (!VACCINE_PLATFORMS.includes(v.platform as VaccinePlatform)) {
          errors.push(`vaccines[${i}].platform '${v.platform}' not in vocabulary`);
        }
        if (typeof v.dosing !== 'string') errors.push(`vaccines[${i}].dosing required`);
        validateCitedFraction(v.efficacy, `vaccines[${i}].efficacy`, errors);
      }
    }
  }

  // --- cross-immunity (optional) ---
  if (r.crossImmunity !== undefined) {
    if (!Array.isArray(r.crossImmunity)) errors.push('crossImmunity must be array');
    else {
      for (let i = 0; i < r.crossImmunity.length; i++) {
        const c = r.crossImmunity[i] as Record<string, unknown>;
        if (typeof c.otherPathogenId !== 'string') errors.push(`crossImmunity[${i}].otherPathogenId required`);
        if (typeof c.reduction !== 'number' || c.reduction < 0 || c.reduction > 1) {
          errors.push(`crossImmunity[${i}].reduction must be in [0, 1]`);
        }
        validateCitations(c.citations, `crossImmunity[${i}].citations`, errors);
      }
    }
  }

  // --- verification, snapshot ---
  if (typeof r.verification !== 'object' || r.verification === null) {
    errors.push('verification required');
  } else {
    const v = r.verification as Record<string, unknown>;
    if (!VERIFICATION_STATUSES.includes(v.status as VerificationStatus)) {
      errors.push(`verification.status must be one of ${VERIFICATION_STATUSES.join(', ')}`);
    }
    if (!Array.isArray(v.reasons)) errors.push('verification.reasons must be array');
  }
  if (typeof r.snapshot !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(r.snapshot)) {
    errors.push('snapshot must be ISO YYYY-MM-DD');
  }

  if (errors.length > 0) return { valid: false, errors };
  return { valid: true, pathogen: r as unknown as Pathogen };
}
